import { digitsOnly } from './otp'

/**
 * The sign-in country list. The backend's `countries_list` (public, no
 * token) is the source: every row is `live` (selectable — the backend
 * really sends a code there) or `coming_soon` (shown, greyed, not
 * selectable), grouped by region in the API's own order. Moving a country
 * from one to the other is a database update, never a frontend release
 * (parrots.club/docs/vonage-rollout.md).
 *
 * FALLBACK_COUNTRIES is what the page shows before that answer arrives, or
 * if it never does: the three countries that were live when this was
 * written. Never more — offering a country the backend cannot reach would
 * silently promise an SMS that never comes.
 */
export const FALLBACK_COUNTRIES = [
  { code: 'IR', name: 'ایران', nameEn: 'Iran', callingCode: '98', flag: '🇮🇷', provider: 'SMS.ir', status: 'live', region: 'iran', phoneHint: '912 345 6789' },
  { code: 'DE', name: 'آلمان', nameEn: 'Germany', callingCode: '49', flag: '🇩🇪', provider: 'Vonage', status: 'live', region: 'eu_eea', phoneHint: '1511 234 5678' },
  { code: 'OM', name: 'عمان', nameEn: 'Oman', callingCode: '968', flag: '🇴🇲', provider: 'Vonage', status: 'live', region: 'gcc', phoneHint: '9123 4567' },
]

/** Kept for the callers and tests that predate the API-driven list. */
export const COUNTRIES = FALLBACK_COUNTRIES

const PROVIDER_NAMES = { sms_ir: 'SMS.ir', vonage: 'Vonage' }

/** One `CountryDto` (backend countries.dto.ts) → the shape this page uses. */
export function countryFromApi(row) {
  return {
    code: row.isoCode,
    name: row.nameFa,
    nameEn: row.nameEn,
    callingCode: row.callingCode,
    flag: row.flagEmoji,
    provider: PROVIDER_NAMES[row.provider] ?? row.provider,
    status: row.status,
    region: row.region,
    phoneHint: row.phoneHint ?? '',
  }
}

/**
 * The API's `items` → the page's list, or null when the answer is unusable
 * (not an array, or no selectable row at all) so the caller keeps the
 * fallback rather than offering an empty or all-grey picker.
 */
export function countriesFromApi(items) {
  if (!Array.isArray(items)) return null
  const list = items
    .filter((row) => row && typeof row.isoCode === 'string' && /^[0-9]{1,4}$/.test(String(row.callingCode)))
    .map(countryFromApi)
  return list.some(isSelectable) ? list : null
}

export function isSelectable(country) {
  return country?.status === 'live'
}

/** Consecutive rows of one region become one group; the API already orders by region, then displayOrder. */
export function groupByRegion(countries) {
  const groups = []
  for (const country of countries) {
    const last = groups[groups.length - 1]
    if (last && last.region === country.region) last.countries.push(country)
    else groups.push({ region: country.region, countries: [country] })
  }
  return groups
}

/**
 * The country to keep selected once the real list arrives: the same code
 * if it is still selectable there, else the list's first selectable row.
 */
export function reconcileSelection(current, countries) {
  const same = countries.find((country) => country.code === current?.code)
  if (isSelectable(same)) return same
  return countries.find(isSelectable) ?? current
}

/** The country's name in the page's language — the site is fully Persian or fully English, never both. */
export function countryName(country, language) {
  return language === 'fa' ? country.name : country.nameEn
}

/** The backend's own validator (auth.dto.ts). */
export const E164 = /^\+[1-9][0-9]{7,14}$/

/**
 * Local number → strict E.164 (`/^\+[1-9][0-9]{7,14}$/`, matching the
 * backend's own validator in auth.dto.ts). A leading 0 on the local number
 * (the usual national trunk prefix: Iran's "0912...", Germany's "0151...",
 * the UK's "07...") is dropped before the calling code is prepended; other
 * digits are just concatenated.
 *
 * Persian and Arabic-Indic digits are converted first (V1-w): the old
 * `replace(/\D/g, '')` alone *deleted* them, so a learner typing «۰۹۱۲…»
 * on a Persian keyboard sent "+98" and was refused.
 */
export function toE164(callingCode, localNumber) {
  const digits = digitsOnly(localNumber).replace(/^0+/, '')
  return `+${callingCode}${digits}`
}

/**
 * Text folded for the picker's search: lower case; Arabic ي/ى and ك read as
 * Persian ی and ک; ة as ه; ZWNJ, spaces, hyphens and dots dropped; Persian
 * and Arabic-Indic digits as Latin. "آلمان", "المان" and "Germany" all match
 * what a learner is likely to type.
 */
export function foldForSearch(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/ة/g, 'ه')
    .replace(/[أإآ]/g, 'ا')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/[\u200c\u200f\u200e\s.\-_]/g, '')
}

/**
 * Whether `country` answers the search `query`: its Persian or English name
 * contains it, its ISO code starts with it, or — for a query of digits, with
 * or without a leading + or 00 — its calling code starts with them ("49",
 * "+49" and "0049" all find Germany). An empty query matches everything.
 */
export function matchesSearch(country, query) {
  const q = foldForSearch(query)
  if (!q) return true
  const digits = q.replace(/^\+/, '').replace(/^00/, '')
  if (/^[0-9]+$/.test(digits)) return String(country.callingCode).startsWith(digits)
  return (
    foldForSearch(country.name).includes(q) ||
    foldForSearch(country.nameEn).includes(q) ||
    foldForSearch(country.code).startsWith(q)
  )
}
